// Post-login Setup Mode selector — 2 cards.
//   1. School Setup by Admin (₹499 paid via Razorpay)
//   2. School Setup by Self (Free)
//
// Admin selection:
//   - Razorpay Checkout opens immediately
//   - On payment success, setupChoice="admin" is set, dashboard shows
//     "Admin team will set up your school" waiting card
// Self selection:
//   - Saves choice instantly, dashboard moves to SetupCompletionPanel
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Zap } from "lucide-react";
import { startRazorpayCheckout } from "@/lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETUP_MODES = [
  {
    id: "admin",
    icon: "🤝",
    badge: "Fastest",
    badgeBg: "bg-amber-500",
    price: "₹499",
    priceSuffix: "one-time",
    title: "School Setup by Admin",
    titleOd: "ଆଡମିନଙ୍କ ଦ୍ୱାରା ସେଟଅପ୍",
    cardBg: "from-amber-50 to-orange-50",
    border: "border-amber-300",
    benefits: [
      "Our team completes full school onboarding",
      "Saves 3-5 hours of work",
      "Faster activation",
      "Ideal for schools wanting hassle-free setup",
    ],
    paid: true,
    planKey: "admin_setup",
    cta: "Pay ₹499 & Get Started",
  },
  {
    id: "self",
    icon: "✋",
    badge: "Free",
    badgeBg: "bg-emerald-600",
    price: "FREE",
    priceSuffix: "",
    title: "School Setup by Self",
    titleOd: "ନିଜେ ସ୍କୁଲ ସେଟଅପ୍",
    cardBg: "from-emerald-50 to-green-50",
    border: "border-emerald-300",
    benefits: [
      "User enters all setup data manually",
      "Save money",
      "Full self-control",
      "Takes approximately 20-40 minutes",
    ],
    paid: false,
    planKey: null,
    cta: "Start Self Setup",
  },
];

export default function ModeOfWorkSelector({ user, onChoose }) {
  const [loading, setLoading] = useState(null); // mode.id while processing

  const handleSelfSetup = async () => {
    setLoading("self");
    try {
      await axios.post(`${API}/auth/setup-choice`, { choice: "self" });
      toast.success("Self-setup mode selected — fill in your school details next.");
      if (onChoose) await onChoose("self");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your choice");
    } finally {
      setLoading(null);
    }
  };

  const handleAdminSetup = async () => {
    setLoading("admin");
    try {
      await startRazorpayCheckout({
        planKey: "admin_setup",
        user,
        onSuccess: async () => {
          // Backend already sets setupChoice="admin" + adminSetupPaid=true in _activate_plan
          toast.success("Payment received! Our admin team will set up your school within 24 hours.");
          if (onChoose) await onChoose("admin");
        },
        onError: (message) => {
          toast.error(`Payment failed: ${message}`);
        },
        onDismiss: () => {
          toast.info("Payment cancelled — you can try again anytime.");
        },
      });
    } catch (err) {
      // Errors already surfaced via toast
    } finally {
      setLoading(null);
    }
  };

  const onCardClick = (mode) => {
    if (mode.id === "self") return handleSelfSetup();
    if (mode.id === "admin") return handleAdminSetup();
  };

  return (
    <section data-testid="mode-of-work-section" className="mb-10">
      <div className="mb-6 text-center">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">
          Step 1 — Choose your setup mode
        </p>
        <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
          How would you like to set up your school?
        </h2>
        <p className="mt-2 text-sm text-ink-700">
          ଆପଣ କିପରି ସ୍କୁଲ ସେଟଅପ୍ କରିବାକୁ ଚାହାଁନ୍ତି?
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="mode-cards-grid">
        {SETUP_MODES.map((mode) => (
          <div
            key={mode.id}
            className={`group relative overflow-hidden rounded-2xl border-2 ${mode.border} bg-gradient-to-br ${mode.cardBg} p-6 shadow-sm transition-all duration-200 sm:p-7`}
            data-testid={`mode-card-${mode.id}`}
          >
            <div className={`absolute right-4 top-4 rounded-full ${mode.badgeBg} px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-white`}>
              {mode.badge}
            </div>
            <div className="text-5xl leading-none">{mode.icon}</div>
            <h3 className="mt-4 font-heading text-xl font-semibold tracking-tight text-ink-900">
              {mode.title}
            </h3>
            <p className="mt-1 text-sm font-medium text-clay-dark">{mode.titleOd}</p>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-mono text-3xl font-bold text-ink-900">{mode.price}</span>
              {mode.priceSuffix && <span className="text-sm text-ink-700">{mode.priceSuffix}</span>}
            </div>

            <ul className="mt-4 space-y-2">
              {mode.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-800">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => onCardClick(mode)}
              disabled={loading !== null}
              className={`mt-6 w-full ${mode.paid ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
              data-testid={`mode-cta-${mode.id}`}
            >
              {loading === mode.id ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
              ) : (
                <>
                  {mode.paid ? <Zap className="mr-2 h-4 w-4" /> : null}
                  {mode.cta}
                </>
              )}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
